import React, { lazy, Suspense, ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { PrivateLayout } from './PrivateLayout';
import { PublicLayout } from './PublicLayout';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { ROUTES } from './routes';
import { ROUTE_PATHS, isVerificationRoute, isIndexRoute } from './routeConstants';

export type RouteModule = () => Promise<{ default: React.ComponentType<any> }>;

const lazyLoad = (importFunc: RouteModule): React.FC<any> => {
    const Component = lazy(async () => {
        try {
            return await importFunc();
        } catch (error) {
            console.error('Failed to load component:', error);
            return { default: () => <div>Failed to load component</div> };
        }
    });

    return (props: any) => (
        <Suspense fallback={<LoadingSpinner type="full-screen" />}>
            <Component {...props} />
        </Suspense>
    );
};

interface RedirectIfAuthenticatedProps {
    children: ReactNode;
}

export const AppRoutes: React.FC = () => {
    const { user } = useAuth();

    const RedirectIfAuthenticated: React.FC<RedirectIfAuthenticatedProps> = ({ children }) => (
        user ? <Navigate to={ROUTE_PATHS.APP} replace /> : <>{children}</>
    );

    return (
        <Routes>
            {/* Public Routes */}
            <Route path={ROUTE_PATHS.AUTH} element={<PublicLayout />}>
                {ROUTES.PUBLIC.map(({ path, component }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            isVerificationRoute(path) ? (
                                lazyLoad(component)()
                            ) : (
                                <RedirectIfAuthenticated>
                                    {lazyLoad(component)()}
                                </RedirectIfAuthenticated>
                            )
                        }
                    />
                ))}
                <Route index element={<Navigate to="login" />} />
            </Route>

            {/* Protected Routes */}
            <Route path={ROUTE_PATHS.APP} element={<ProtectedRoute><PrivateLayout /></ProtectedRoute>}>
                {ROUTES.PRIVATE.map(({ path, component }) => (
                    <Route
                        key={path || 'index'}
                        path={path}
                        index={isIndexRoute(path)}
                        element={lazyLoad(component)()}
                    />
                ))}

                {/* Admin Routes */}
                {ROUTES.ADMIN.map(({ path, component }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <ProtectedRoute adminOnly>
                                {lazyLoad(component)()}
                            </ProtectedRoute>
                        }
                    />
                ))}
            </Route>

            {/* Catch-all redirect */}
            <Route
                path="*"
                element={user ? <Navigate to={ROUTE_PATHS.APP} replace /> : <Navigate to={ROUTE_PATHS.AUTH} replace />}
            />
        </Routes>
    );
};

export default AppRoutes;